import { useQuery } from "@apollo/client";
import UserAvatar from "components/User/UserAvatar";
import { LiquidPill } from "components/ui/v2";
import gqlMagazine from "gql/Magazine";
import { useVendor } from "context/vendor/vendor.provider";
import { BookOpen, Sparkles } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { useRouter } from "next/router";
import type { IMagazineEdition } from "types/Magazine.type";
import { magazineHasContent, resolveMagazineName } from "components/ui/v2/magazine/magazine-utils";

import SideCard from "../SideCard/SideCard";
import SideCardV2 from "../SideCard/SideCardV2";

interface MagazineWidgetProps {
  readonly variant?: "default" | "v2";
}

/**
 * Magazine widget for the Explore side panel.
 * Shows the latest published edition (preview or icon fallback).
 * CTA navigates to `/app/magazine`.
 */
const MagazineWidget = (props: MagazineWidgetProps) => {
  const { variant = "default" } = props;
  const { t } = useTranslation("common");
  const router = useRouter();
  const { vendor } = useVendor();
  const magazineName = resolveMagazineName(vendor, t("Magazine"));

  if (!vendor?.enableMagazine) {
    return null;
  }

  const { data, loading } = useQuery<{
    getLatestMagazineEdition: IMagazineEdition | null;
  }>(gqlMagazine.queries.GET_LATEST_MAGAZINE_EDITION, {
    fetchPolicy: "no-cache",
  });

  const latest = data?.getLatestMagazineEdition;
  const hasContent = magazineHasContent(latest ?? undefined);

  const handleViewEdition = () => {
    if (latest?.id) {
      router.push(`/app/magazine?edition=${latest.id}`);
      return;
    }
    router.push("/app/magazine");
  };

  const handleViewAll = () => {
    router.push("/app/magazine");
  };

  const ActiveSideCard = (variant === "v2" ? SideCardV2 : SideCard) as typeof SideCard;
  const initialLoading = loading && !data;

  if (initialLoading) {
    return (
      <ActiveSideCard
        title={magazineName}
        loading
        variant={variant}
        collapsible={false}
        defaultExpanded
        icon={
          variant === "v2" ? (
            <BookOpen size={16} strokeWidth={1} color="#8B5CF6" />
          ) : null
        }
      >
        <span />
      </ActiveSideCard>
    );
  }

  if (!latest?.id || !hasContent) return null;

  const authorName = latest.createdBy
    ? [latest.createdBy.name, latest.createdBy.lastname].filter(Boolean).join(" ")
    : "";

  return (
    <>
      <style jsx global>{`
        @keyframes explore-ranking-person-enter {
          0% {
            opacity: 0;
            transform: translateY(14px) scale(0.94);
            filter: blur(8px);
          }
          58% {
            opacity: 1;
            transform: translateY(-2px) scale(1.018);
            filter: blur(0);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }
        @keyframes explore-ranking-avatar-pop {
          0% {
            opacity: 0;
            transform: scale(0.72) rotate(-4deg);
          }
          68% {
            opacity: 1;
            transform: scale(1.07) rotate(1deg);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }
        @keyframes explore-ranking-copy-rise {
          0% {
            opacity: 0;
            transform: translateY(7px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .explore-ranking-person-enter {
          animation: explore-ranking-person-enter 560ms
            cubic-bezier(0.22, 1, 0.36, 1) both;
          will-change: transform, opacity, filter;
        }
        .explore-ranking-person-enter .explore-ranking-avatar-pop {
          animation: explore-ranking-avatar-pop 620ms
            cubic-bezier(0.34, 1.56, 0.64, 1) 70ms both;
        }
        .explore-ranking-person-enter .explore-ranking-copy-rise {
          animation: explore-ranking-copy-rise 420ms
            cubic-bezier(0.22, 1, 0.36, 1) 150ms both;
        }
      `}</style>
      <ActiveSideCard
        title={magazineName}
        variant={variant}
        collapsible={false}
        defaultExpanded
        buttonText={t("Ver edición")}
        onViewMore={handleViewAll}
        icon={
          variant === "v2" ? (
            <BookOpen size={16} strokeWidth={1} color="#8B5CF6" />
          ) : null
        }
      >
        {variant === "v2" && (
          <div className="space-y-2.5">
            <div
              key={latest.id}
              className="explore-ranking-person-enter relative px-3 py-2"
            >
              <div className="relative flex flex-col items-center text-center">
                <button
                  type="button"
                  onClick={handleViewEdition}
                  className="explore-ranking-avatar-pop overflow-hidden rounded-2xl ring-2 ring-white/90 ring-offset-2 ring-offset-white/60 transition-transform hover:scale-[1.02] dark:ring-zinc-900 dark:ring-offset-zinc-800"
                  aria-label={latest.title}
                >
                  {latest.previewImage ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={latest.previewImage}
                      alt={latest.title}
                      className="h-24 w-24 object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-24 w-24 items-center justify-center bg-gradient-to-br from-violet-100 to-indigo-200 dark:from-violet-950 dark:to-indigo-950">
                      <Sparkles
                        className="h-10 w-10 text-violet-400 dark:text-violet-300"
                        strokeWidth={1.4}
                      />
                    </div>
                  )}
                </button>

                <p
                  className="explore-ranking-copy-rise mt-2 line-clamp-2 max-w-full break-words px-2 text-[14px] font-semibold leading-tight text-slate-900 dark:text-zinc-100"
                  title={latest.title}
                >
                  {latest.title}
                </p>

                <LiquidPill
                  variant="violet"
                  appearance="tinted"
                  size="xs"
                  text={t("Última edición")}
                  className="explore-ranking-copy-rise mt-1"
                />

                {latest.createdBy &&
                  (authorName || latest.createdBy.profileImage) && (
                    <div className="explore-ranking-copy-rise mt-2 flex items-center gap-1.5 text-xs text-slate-500 dark:text-zinc-400">
                      <UserAvatar user={latest.createdBy as any} size={18} />
                      <span className="max-w-[10rem] truncate">
                        {authorName || t("Editor")}
                      </span>
                    </div>
                  )}
              </div>
            </div>
          </div>
        )}

        {variant === "default" && (
          <button
            type="button"
            onClick={handleViewEdition}
            className="flex w-full items-center gap-2.5 rounded-md p-1.5 text-left transition-colors hover:bg-slate-50 dark:hover:bg-zinc-800/50"
          >
            <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md bg-violet-100 dark:bg-violet-950/40">
              {latest.previewImage ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={latest.previewImage}
                  alt={latest.title}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <Sparkles className="h-4 w-4 text-violet-500" />
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold text-slate-800 dark:text-zinc-200">
                {latest.title}
              </p>
              <p className="text-[11px] text-violet-600 dark:text-violet-400">
                {t("Última edición")}
              </p>
            </div>
          </button>
        )}
      </ActiveSideCard>
    </>
  );
};

export default MagazineWidget;
